import { endOfMonth, format, startOfMonth } from "date-fns";
import { useEffect } from "react";

import { DATE_FORMATS_FNS } from "components";
import { useTypedQuery } from "hooks";
import { ExactFailuresQueryKeys } from "modules/ExactMonitoring/queryKeys";
import { ApiInstance } from "utils/Api";

import type { ExactFailedInvoice } from "models";
import type { InvoicedDealersAndLocationsFilters, InvoicedDealersAndLocationsResponse } from "modules/ExactMonitoring/queryKeys";

export type DealersAndLocationsWithFailedInvoices = {
  invoiceError: string | null;
  lastInvoiceDate: string | null;
  dealerId?: number;
  dealerName?: string;
  locationId?: number;
  locationName?: string;
  exactFailedInvoices?: ExactFailedInvoice[];
};

const normalizeData = (invoicedDealersAndLocations?: InvoicedDealersAndLocationsResponse) => {
  const results: DealersAndLocationsWithFailedInvoices[] = [];

  if (!invoicedDealersAndLocations) return results;

  for (const d of invoicedDealersAndLocations.dealers) {
    results.push({
      dealerName: d.name,
      dealerId: d.id,
      lastInvoiceDate: d.last_invoice_time || null,
      invoiceError: d.invoice_error || null,
      exactFailedInvoices: d.exact_failed_invoices
    });
  }

  for (const l of invoicedDealersAndLocations.dealer_locations) {
    results.push({
      dealerName: l.dealer_name,
      locationName: l.name,
      locationId: l.id,
      lastInvoiceDate: l.last_invoice_time,
      invoiceError: l.invoice_error,
      exactFailedInvoices: l.exact_failed_invoices
    });
  }

  return results;
};

const generateInvoicedDealersAndLocationsPayload = (filters: InvoicedDealersAndLocationsFilters) => {
  return {
    ...filters,
    date_from: filters.date_from
      ? format(filters.date_from, DATE_FORMATS_FNS.ISOFormatStartOfDay)
      : format(startOfMonth(new Date()), DATE_FORMATS_FNS.ISOFormatStartOfDay),
    date_to: filters.date_to ? format(filters.date_to, DATE_FORMATS_FNS.ISOFormatEndOfDay) : format(endOfMonth(new Date()), DATE_FORMATS_FNS.ISOFormatEndOfDay)
  };
};

export const useInvoicedDealersAndLocationsQuery = (filters: InvoicedDealersAndLocationsFilters) => {
  const query = useTypedQuery({
    queryKey: ExactFailuresQueryKeys.invoicedDealersAndLocations,
    queryFn: async ({ baseUrl, endpoint }) => (await ApiInstance.post(endpoint, generateInvoicedDealersAndLocationsPayload(filters), baseUrl)).data,
    enabled: false,
    select: normalizeData
  });

  //TODO: check for common approach in this situation, where you have something in the dependency but it is not actively being used in the hook itself, to some it could be confusing (in this case it is filters.failed)
  useEffect(() => {
    if (filters.date_from && filters.date_to) query.refetch();
  }, [filters.date_from, filters.date_to, filters.failed]);

  return query;
};
